import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Car, Bus, Plus, Check, X, Download, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const RATE_PER_KM = { private: 3.5, bus: 2.0 };
const inr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function TravelAllowancePage() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'management'].includes(user?.system_role);
  const isBranchManager = user?.system_role === 'branch_manager';
  const isHR = user?.job_role === 'H.R';
  const canAccess = isAdmin || isHR || isBranchManager;
  // Anyone with TA access can ENTER a request (Branch Manager raises for self/team).
  // Approval stays SA + Management only.
  const canEnter = isAdmin || isBranchManager;
  const canApprove = isAdmin;

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [tab, setTab] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null); // drill-in

  const [filterBranch, setFilterBranch] = useState('');
  const [filterDivision, setFilterDivision] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [dialog, setDialog] = useState({ open: false });
  const [form, setForm] = useState({ employee_id: '', date: new Date().toISOString().slice(0, 10), from_location: '', to_location: '', mode: 'private', km: '', notes: '' });

  const fetchAll = useCallback(async () => {
    if (!canAccess) { setLoading(false); return; }
    setLoading(true);
    setEntries([]);
    try {
      const [rRes, eRes, bRes] = await Promise.all([
        api.get('/ta-report', { params: { month, branch_id: filterBranch || undefined, division: filterDivision || undefined, employee_id: filterEmployee || undefined, include_pending: filterStatus !== 'approved' } }),
        api.get('/employees'),
        api.get('/branches'),
      ]);
      setReport(rRes.data);
      setEmployees(eRes.data || []);
      setBranches(bRes.data || []);
      const eeRes = await api.get('/ta-entries', { params: { month, branch_id: filterBranch || undefined, division: filterDivision || undefined, employee_id: filterEmployee || undefined, status: filterStatus } });
      setEntries(eeRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [month, filterBranch, filterDivision, filterEmployee, filterStatus, canAccess]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!canAccess) {
    return <div className="p-6 text-neutral-500">Access denied. This page is available to Super Admin, Management, and HR only.</div>;
  }

  const handleCreate = async () => {
    if (!form.employee_id || !form.date || !form.from_location.trim() || !form.to_location.trim() || !form.km) {
      toast.error('Fill all required fields'); return;
    }
    if (parseFloat(form.km) <= 0) { toast.error('KM must be > 0'); return; }
    try {
      await api.post('/ta-entries', {
        employee_id: form.employee_id,
        date: form.date,
        from_location: form.from_location.trim(),
        to_location: form.to_location.trim(),
        mode: form.mode,
        km: parseFloat(form.km),
        notes: form.notes,
      });
      toast.success(`TA entry created · ${inr(parseFloat(form.km) * RATE_PER_KM[form.mode])}`);
      setDialog({ open: false });
      setForm({ employee_id: '', date: new Date().toISOString().slice(0, 10), from_location: '', to_location: '', mode: 'private', km: '', notes: '' });
      fetchAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleApprove = async (id) => {
    try { await api.put(`/ta-entries/${id}/approve`); toast.success('Approved'); fetchAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const handleReject = async (id) => {
    const reason = window.prompt('Reason for rejection?'); if (!reason) return;
    try { await api.put(`/ta-entries/${id}/reject`, { reason }); toast.success('Rejected'); fetchAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const handleDelete = async (e) => {
    if (!window.confirm(`Delete TA entry for ${e.employee_name} on ${e.date}?`)) return;
    try { await api.delete(`/ta-entries/${e.id}`); toast.success('Deleted'); fetchAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const exportToExcel = () => {
    if (!report) return;
    // Build CSV respecting current filters — opens directly in Excel
    const lines = [];
    lines.push(['Month', month].join(','));
    if (filterDivision) lines.push(['Division', filterDivision].join(','));
    if (filterBranch) lines.push(['Branch', branches.find(b => b.id === filterBranch)?.name || filterBranch].join(','));
    lines.push('');
    lines.push('SUMMARY');
    lines.push(['Employee ID', 'Employee Name', 'Division', 'Branch', 'TA Days', 'Private KM', 'Bus KM', 'Private TA', 'Bus TA', 'Total TA', 'Payroll Synced'].join(','));
    (report.rows || []).forEach(r => {
      lines.push([r.employee_code || '', `"${r.employee_name}"`, r.division || '', `"${r.branch_name || ''}"`, r.ta_days, r.total_km_private, r.total_bus_km, r.private_total, r.bus_total, r.total_amount, r.payroll_synced ? 'Yes' : 'No'].join(','));
    });
    lines.push('');
    lines.push('DETAILED ENTRIES');
    lines.push(['Date', 'Employee Name', 'From', 'To', 'Mode', 'KM', 'Rate', 'Amount', 'Status', 'Notes'].join(','));
    entries.forEach(e => {
      lines.push([e.date, `"${e.employee_name}"`, `"${e.from_location}"`, `"${e.to_location}"`, e.mode, e.km, e.rate_per_km, e.amount, e.status, `"${(e.notes || '').replace(/"/g, "'")}"`].join(','));
    });
    lines.push('');
    lines.push(['TOTAL EMPLOYEES', report.summary.total_employees].join(','));
    lines.push(['TOTAL TA AMOUNT', report.summary.total_ta_amount].join(','));
    lines.push(['TOTAL PRIVATE KM', report.summary.total_km_private].join(','));
    lines.push(['TOTAL BUS KM', report.summary.total_bus_km].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `TA-Report-${month}.csv`;
    a.click();
  };

  const divisions = Array.from(new Set(employees.map(e => e.division).filter(Boolean)));
  const filteredEmployees = employees.filter(e =>
    (!filterBranch || e.branch_id === filterBranch) &&
    (!filterDivision || e.division === filterDivision)
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="travel-allowance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Travel Allowance</h1>
          <p className="text-neutral-500 text-sm mt-0.5">
            Private: ₹{RATE_PER_KM.private}/km &middot; Bus: ₹{RATE_PER_KM.bus}/km &middot; Approved entries auto-sync to payroll
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-sm" onClick={exportToExcel} data-testid="ta-export">
            <Download className="h-4 w-4 mr-2" /> Export to Excel
          </Button>
          {canEnter && (
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => setDialog({ open: true })} data-testid="ta-new-entry">
              <Plus className="h-4 w-4 mr-2" /> New TA Entry
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 flex-wrap items-end">
          <div className="space-y-1 flex-1 min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Month *</Label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-10 w-full px-3 border border-neutral-200 rounded-sm text-sm bg-white" data-testid="ta-filter-month" />
          </div>
          <div className="space-y-1 flex-1 min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Division</Label>
            <Select value={filterDivision || '__all__'} onValueChange={v => setFilterDivision(v === '__all__' ? '' : v)}>
              <SelectTrigger className="rounded-sm" data-testid="ta-filter-division"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Divisions</SelectItem>
                {divisions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Branch</Label>
            <Select value={filterBranch || '__all__'} onValueChange={v => setFilterBranch(v === '__all__' ? '' : v)}>
              <SelectTrigger className="rounded-sm" data-testid="ta-filter-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Employee</Label>
            <Select value={filterEmployee || '__all__'} onValueChange={v => setFilterEmployee(v === '__all__' ? '' : v)}>
              <SelectTrigger className="rounded-sm" data-testid="ta-filter-employee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Employees</SelectItem>
                {filteredEmployees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="rounded-sm" data-testid="ta-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary totals */}
      {report?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border border-neutral-200 rounded-md" data-testid="ta-sum-employees"><CardContent className="p-3"><p className="text-[10px] uppercase text-neutral-400">Employees</p><p className="text-xl font-bold">{report.summary.total_employees}</p></CardContent></Card>
          <Card className="border border-neutral-200 rounded-md" data-testid="ta-sum-entries"><CardContent className="p-3"><p className="text-[10px] uppercase text-neutral-400">TA Days</p><p className="text-xl font-bold">{report.summary.total_entries}</p></CardContent></Card>
          <Card className="border border-neutral-200 rounded-md" data-testid="ta-sum-km-private"><CardContent className="p-3"><p className="text-[10px] uppercase text-neutral-400">Private KM</p><p className="text-xl font-bold">{report.summary.total_km_private}</p></CardContent></Card>
          <Card className="border border-neutral-200 rounded-md" data-testid="ta-sum-km-bus"><CardContent className="p-3"><p className="text-[10px] uppercase text-neutral-400">Bus KM</p><p className="text-xl font-bold">{report.summary.total_bus_km}</p></CardContent></Card>
          <Card className="border border-emerald-200 bg-emerald-50/40 rounded-md" data-testid="ta-sum-total"><CardContent className="p-3"><p className="text-[10px] uppercase text-neutral-400">Total TA</p><p className="text-xl font-bold text-emerald-700">{inr(report.summary.total_ta_amount)}</p></CardContent></Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="entries" data-testid="ta-tab-entries">Entries</TabsTrigger>
          <TabsTrigger value="summary" data-testid="ta-tab-summary">Employee Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="entries" className="mt-4">
          <Card className="border border-neutral-200 shadow-sm rounded-md">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Daily Entries ({entries.length})</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-neutral-50">
                      <TableHead className="text-xs uppercase">Date</TableHead>
                      <TableHead className="text-xs uppercase">Employee</TableHead>
                      <TableHead className="text-xs uppercase">From → To</TableHead>
                      <TableHead className="text-xs uppercase">Mode</TableHead>
                      <TableHead className="text-xs uppercase text-right">KM</TableHead>
                      <TableHead className="text-xs uppercase text-right">Amount</TableHead>
                      <TableHead className="text-xs uppercase">Status</TableHead>
                      {(canApprove || canEnter) && <TableHead className="text-xs uppercase text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={8} className="py-8 text-center text-neutral-400">Loading...</TableCell></TableRow>
                    ) : entries.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="py-10 text-center text-neutral-500">No TA entries for {month}</TableCell></TableRow>
                    ) : entries.map(e => (
                      <TableRow key={e.id}>
                        <TableCell className="text-sm">{e.date}</TableCell>
                        <TableCell className="text-sm font-medium">{e.employee_name}</TableCell>
                        <TableCell className="text-sm text-neutral-700">{e.from_location} → {e.to_location}</TableCell>
                        <TableCell>
                          {e.mode === 'private' ? (
                            <Badge className="text-[10px] bg-blue-100 text-blue-700"><Car className="h-3 w-3 mr-1 inline" />Private</Badge>
                          ) : (
                            <Badge className="text-[10px] bg-amber-100 text-amber-700"><Bus className="h-3 w-3 mr-1 inline" />Bus</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-right tabular-nums">{e.km}</TableCell>
                        <TableCell className="text-sm text-right font-semibold tabular-nums">{inr(e.amount)}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] capitalize ${e.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : e.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{e.status}</Badge>
                        </TableCell>
                        {(canApprove || canEnter) && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {canApprove && e.status === 'pending' && (
                                <>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleApprove(e.id)} data-testid={`ta-approve-${e.id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleReject(e.id)} data-testid={`ta-reject-${e.id}`}><X className="h-4 w-4 text-red-500" /></Button>
                                </>
                              )}
                              {canEnter && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(e)} data-testid={`ta-delete-${e.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="summary" className="mt-4">
          {selectedEmp ? (
            <EmployeeDetail emp={selectedEmp} month={month} onBack={() => setSelectedEmp(null)} />
          ) : (
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Employee Summary ({report?.rows?.length || 0})</CardTitle></CardHeader>
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-neutral-50">
                        <TableHead className="text-xs uppercase">Code</TableHead>
                        <TableHead className="text-xs uppercase">Employee</TableHead>
                        <TableHead className="text-xs uppercase">Division</TableHead>
                        <TableHead className="text-xs uppercase">Branch</TableHead>
                        <TableHead className="text-xs uppercase text-right">TA Days</TableHead>
                        <TableHead className="text-xs uppercase text-right">Private KM</TableHead>
                        <TableHead className="text-xs uppercase text-right">Bus Total</TableHead>
                        <TableHead className="text-xs uppercase text-right">Private Total</TableHead>
                        <TableHead className="text-xs uppercase text-right">Total TA</TableHead>
                        <TableHead className="text-xs uppercase">Payroll</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(!report || report.rows.length === 0) ? (
                        <TableRow><TableCell colSpan={10} className="py-10 text-center text-neutral-500">No data</TableCell></TableRow>
                      ) : report.rows.map(r => (
                        <TableRow key={r.employee_id} className="hover:bg-neutral-50 cursor-pointer" onClick={() => setSelectedEmp(r)} data-testid={`ta-row-${r.employee_id}`}>
                          <TableCell className="text-xs text-neutral-500">{r.employee_code || '-'}</TableCell>
                          <TableCell className="text-sm font-medium">{r.employee_name}</TableCell>
                          <TableCell className="text-sm">{r.division || '-'}</TableCell>
                          <TableCell className="text-sm">{r.branch_name || '-'}</TableCell>
                          <TableCell className="text-sm text-right">{r.ta_days}</TableCell>
                          <TableCell className="text-sm text-right">{r.total_km_private}</TableCell>
                          <TableCell className="text-sm text-right text-amber-700">{inr(r.bus_total)}</TableCell>
                          <TableCell className="text-sm text-right text-blue-700">{inr(r.private_total)}</TableCell>
                          <TableCell className="text-sm text-right font-bold text-emerald-700">{inr(r.total_amount)}</TableCell>
                          <TableCell>
                            {r.payroll_synced ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Synced</Badge> :
                              r.payroll_status ? <Badge className="text-[10px] bg-amber-100 text-amber-700">Pending sync</Badge> :
                              <Badge className="text-[10px] bg-neutral-100 text-neutral-600">No payroll</Badge>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ open: o })}>
        <DialogContent className="max-w-md" data-testid="ta-create-dialog">
          <DialogHeader><DialogTitle className="font-heading font-bold">New TA Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-sm">Employee *</Label>
              <Select value={form.employee_id} onValueChange={v => setForm(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="ta-form-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} — {e.employee_code || e.job_role}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="rounded-sm" data-testid="ta-form-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Mode *</Label>
                <Select value={form.mode} onValueChange={v => setForm(p => ({ ...p, mode: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="ta-form-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private Vehicle (₹3.5/km)</SelectItem>
                    <SelectItem value="bus">Bus (₹2/km)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">From *</Label>
                <Input value={form.from_location} onChange={e => setForm(p => ({ ...p, from_location: e.target.value }))} className="rounded-sm" data-testid="ta-form-from" placeholder="Kochi" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">To *</Label>
                <Input value={form.to_location} onChange={e => setForm(p => ({ ...p, to_location: e.target.value }))} className="rounded-sm" data-testid="ta-form-to" placeholder="Thrissur" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Distance (KM) *</Label>
              <Input type="number" min="0" step="0.1" value={form.km} onChange={e => setForm(p => ({ ...p, km: e.target.value }))} className="rounded-sm" data-testid="ta-form-km" placeholder="e.g., 82" />
              {form.km && parseFloat(form.km) > 0 && (
                <p className="text-xs text-emerald-700 font-medium">= {inr(parseFloat(form.km) * RATE_PER_KM[form.mode])}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog({ open: false })}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="ta-form-submit">Create & Submit for Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmployeeDetail({ emp, month, onBack }) {
  const [details, setDetails] = useState([]);
  useEffect(() => {
    api.get(`/ta-report/employees/${emp.employee_id}`, { params: { month } })
      .then(r => setDetails(r.data || []))
      .catch(console.error);
  }, [emp.employee_id, month]);

  return (
    <Card className="border border-neutral-200 shadow-sm rounded-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold">{emp.employee_name} · {month}</CardTitle>
            <p className="text-xs text-neutral-500 mt-1">{emp.branch_name} · {emp.division || 'No division'}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onBack} className="rounded-sm" data-testid="ta-drill-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className="border rounded-sm p-2"><p className="text-[10px] uppercase text-neutral-400">Days</p><p className="font-bold">{emp.ta_days}</p></div>
          <div className="border rounded-sm p-2"><p className="text-[10px] uppercase text-neutral-400">Private KM</p><p className="font-bold">{emp.total_km_private}</p></div>
          <div className="border rounded-sm p-2"><p className="text-[10px] uppercase text-neutral-400">Bus Total</p><p className="font-bold text-amber-700">{inr(emp.bus_total)}</p></div>
          <div className="border rounded-sm p-2 bg-emerald-50"><p className="text-[10px] uppercase text-neutral-400">Total TA</p><p className="font-bold text-emerald-700">{inr(emp.total_amount)}</p></div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-xs uppercase">Date</TableHead>
              <TableHead className="text-xs uppercase">From → To</TableHead>
              <TableHead className="text-xs uppercase">Mode</TableHead>
              <TableHead className="text-xs uppercase text-right">KM</TableHead>
              <TableHead className="text-xs uppercase text-right">Amount</TableHead>
              <TableHead className="text-xs uppercase">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {details.map(d => (
              <TableRow key={d.id}>
                <TableCell className="text-sm">{d.date}</TableCell>
                <TableCell className="text-sm">{d.from_location} → {d.to_location}</TableCell>
                <TableCell className="text-xs capitalize">{d.mode}</TableCell>
                <TableCell className="text-sm text-right">{d.km}</TableCell>
                <TableCell className="text-sm text-right font-semibold">{inr(d.amount)}</TableCell>
                <TableCell><Badge className={`text-[10px] capitalize ${d.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : d.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{d.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
